import { useCallback, useEffect, useState } from "react";
import { trpc } from "../../hooks/trpc";
import { UserPlus, Trash2, ShieldCheck, X, RotateCcw, Download, FileText, Key, Copy } from "lucide-react";
import { LabelLine } from "../brand";
import { INSTANCE } from "../../lib/instance";

// Neue Konten nutzen den Login Anbieter der Instanz (AUTH_PROVIDER).
const SSO_PROVIDER: "microsoft" | "google" = INSTANCE.authProvider;
const SSO_LABEL = SSO_PROVIDER === "google" ? "Google" : "Microsoft";

// Benutzerkennung = users.id. Genau diesen Wert schickt der Hub beim
// SSO Exchange als userId an die Satelliten (server/index.ts, Launch Route,
// `userId: String(auth.userId)`). Nexus braucht ihn z. B. fuer E2E_USER_ID.
export function UserIdCopyField({ userId, onCopied }: { userId: number; onCopied: (message: string) => void }) {
  const value = String(userId);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      onCopied(`Kennung ${value} kopiert.`);
    } catch {
      onCopied("Kopieren nicht moeglich. Kennung bitte manuell markieren.");
    }
  };
  return (
    <div className="flex items-center gap-1 mt-1">
      <code
        data-testid={`admin-user-id-${userId}`}
        title="Benutzerkennung (userId beim SSO Exchange)"
        className="text-[11px]"
        style={{
          fontFamily: "var(--font-mono)",
          color: "var(--color-muted-foreground)",
          border: "1px solid var(--color-border)",
          borderRadius: 4,
          padding: "1px 6px",
          userSelect: "all",
        }}
      >
        {value}
      </code>
      <button
        type="button"
        onClick={copy}
        title="Kennung kopieren"
        aria-label={`Kennung ${value} kopieren`}
        className="btn btn-secondary py-0 px-1 text-[11px]"
      >
        <Copy size={11} strokeWidth={1.5} /> Kopieren
      </button>
    </div>
  );
}

function CopyToast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3000);
    return () => clearTimeout(t);
  }, [message, onDismiss]);
  return (
    <div
      role="status"
      data-testid="admin-user-id-toast"
      style={{
        position: "fixed", bottom: 20, right: 20, zIndex: 60,
        background: "var(--color-success-bg)", color: "var(--color-success)",
        border: "1px solid var(--color-success)", padding: "10px 14px",
        borderRadius: 6, fontFamily: "var(--font-mono)", fontSize: 12,
      }}
    >
      {message}
    </div>
  );
}

// Einzige Benutzerverwaltung des Hubs (Adminbereich, Ansicht "users").
// Zusammengelegt aus dem früheren Reiter Admin und der alten
// Benutzerverwaltung; Rollen und App-Rechte pflegt die Ansicht "roles".
export function UserManagementView() {
  const usersQuery = trpc.admin.listUsers.useQuery();
  const rolesQuery = trpc.admin.listRoles.useQuery();
  const userRolesQuery = trpc.admin.rbacUserRoles.useQuery();
  const createMutation = trpc.admin.createUser.useMutation({ onSuccess: () => usersQuery.refetch() });
  const deleteMutation = trpc.admin.deleteUser.useMutation({ onSuccess: () => usersQuery.refetch() });
  const reactivateMutation = trpc.admin.reactivateUser.useMutation({ onSuccess: () => usersQuery.refetch() });
  const utils = trpc.useUtils();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    username: "",
    password: "",
    name: "",
    email: "",
    roleId: 0,
    authProvider: SSO_PROVIDER as "microsoft" | "google" | "local",
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [showExport, setShowExport] = useState(false);
  const [exportCriteria, setExportCriteria] = useState({ userId: "", personalnummer: "", email: "" });
  const [exportBusy, setExportBusy] = useState(false);
  const setUserRoleMutation = trpc.admin.rbacSetUserRole.useMutation({
    onSuccess: () => { usersQuery.refetch(); userRolesQuery.refetch(); },
    onError: (err) => { alert(`Fehler: ${err.message}`); },
  });
  const resetPasswordMutation = trpc.admin.resetUserPassword.useMutation();

  const [pwResetTarget, setPwResetTarget] = useState<{ id: number; username: string } | null>(null);
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState<string | null>(null);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const dismissCopyToast = useCallback(() => setCopyToast(null), []);
  const userRows = usersQuery.data ?? [];
  const roles = rolesQuery.data ?? [];
  const roleAssignments = userRolesQuery.data ?? [];

  const roleLabel = (roleId: number | null | undefined, fallback: string) => {
    const role = roles.find((candidate) => candidate.id === roleId);
    return role?.label ?? role?.key ?? fallback;
  };

  const closePwModal = () => {
    setPwResetTarget(null);
    setPwNew("");
    setPwConfirm("");
    setPwError(null);
  };

  const handlePwSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    if (!pwResetTarget) return;
    if (pwNew.length < 12) {
      setPwError("Passwort muss mindestens 12 Zeichen lang sein.");
      return;
    }
    if (!/[a-z]/.test(pwNew) || !/[A-Z]/.test(pwNew) || !/\d/.test(pwNew)) {
      setPwError("Passwort muss Gross- und Kleinbuchstaben sowie mindestens eine Ziffer enthalten.");
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError("Passwoerter stimmen nicht ueberein.");
      return;
    }
    try {
      await resetPasswordMutation.mutateAsync({ id: pwResetTarget.id, newPassword: pwNew });
      setPwSuccess(`Passwort fuer "${pwResetTarget.username}" wurde zurueckgesetzt. Der Benutzer wurde ausgeloggt.`);
      closePwModal();
      setTimeout(() => setPwSuccess(null), 5000);
    } catch (err) {
      setPwError((err as Error).message || "Passwort-Reset fehlgeschlagen.");
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Client-seitige Validierung
    if (form.username.trim().length < 3) {
      setFormError("Benutzername muss mindestens 3 Zeichen lang sein.");
      return;
    }
    // Password only required for Service Accounts (local)
    if (form.authProvider === "local") {
      if (form.password.length < 10 || !/[a-z]/.test(form.password) || !/[A-Z]/.test(form.password) || !/\d/.test(form.password)) {
        setFormError("Passwort: mind. 10 Zeichen, Gross- und Kleinbuchstaben sowie Ziffer.");
        return;
      }
    }
    if (!form.roleId) {
      setFormError("Bitte eine Rolle auswaehlen.");
      return;
    }
    if (form.authProvider !== "local" && !form.email.trim()) {
      setFormError(`${SSO_LABEL} Benutzer benoetigen eine E-Mail-Adresse.`);
      return;
    }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setFormError("Ungueltige E-Mail-Adresse.");
      return;
    }

    const payload = {
      username: form.username.trim(),
      roleId: form.roleId,
      authProvider: form.authProvider,
      ...(form.authProvider === "local" ? { password: form.password } : {}),
      ...(form.name.trim() ? { name: form.name.trim() } : {}),
      ...(form.email.trim() ? { email: form.email.trim() } : {}),
    };

    try {
      await createMutation.mutateAsync(payload);
      setShowForm(false);
      setForm({ username: "", password: "", name: "", email: "", roleId: 0, authProvider: SSO_PROVIDER });
    } catch (err) {
      setFormError((err as Error).message || "Anlegen fehlgeschlagen.");
    }
  };

  const handleExport = async (e: React.FormEvent) => {
    e.preventDefault();
    setExportBusy(true);
    try {
      const criteria: { userId?: number; personalnummer?: string; email?: string } = {};
      if (exportCriteria.userId) criteria.userId = Number(exportCriteria.userId);
      if (exportCriteria.personalnummer) criteria.personalnummer = exportCriteria.personalnummer;
      if (exportCriteria.email) criteria.email = exportCriteria.email;
      const data = await utils.admin.exportPersonData.fetch(criteria);
      const tag = criteria.userId ?? criteria.personalnummer ?? criteria.email ?? "unknown";
      const filename = `dsgvo-export_${tag}_${new Date().toISOString().slice(0, 10)}.json`;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      setShowExport(false);
      setExportCriteria({ userId: "", personalnummer: "", email: "" });
    } catch (err) {
      alert("Export fehlgeschlagen: " + (err as Error).message);
    } finally {
      setExportBusy(false);
    }
  };

  return (
    <div className="animate-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <p
          className="text-xs"
          style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
        >
          {userRows.length} Benutzer registriert
        </p>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setShowExport(!showExport); setShowForm(false); }} className="btn btn-secondary">
            <FileText size={14} strokeWidth={1.5} /> DSGVO Auskunft
          </button>
          <button onClick={() => { setShowForm(!showForm); setFormError(null); }} className="btn btn-primary">
            {showForm ? <><X size={14} strokeWidth={1.5} /> Abbrechen</> : <><UserPlus size={14} strokeWidth={1.5} /> Neuer Benutzer</>}
          </button>
        </div>
      </div>

      {showExport && (
        <form onSubmit={handleExport} className="card p-6 mb-6 animate-in">
          <h3
            className="text-sm font-semibold mb-2"
            style={{ fontFamily: "var(--font-display)", color: "var(--color-foreground)" }}
          >
            DSGVO Art. 15 – Auskunftsersuchen
          </h3>
          <p
            className="text-xs mb-5"
            style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-body)" }}
          >
            Liefert alle personenbezogenen Daten aus users, core.mitarbeiter, core.externe, mitarbeiter (legacy) und audit_log als JSON. Mindestens ein Kriterium angeben.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>Benutzer-ID</LabelLine>
              <input className="input mt-2" value={exportCriteria.userId} onChange={e => setExportCriteria({ ...exportCriteria, userId: e.target.value })} placeholder="z. B. 3" />
            </div>
            <div>
              <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>Personalnummer</LabelLine>
              <input className="input mt-2" value={exportCriteria.personalnummer} onChange={e => setExportCriteria({ ...exportCriteria, personalnummer: e.target.value })} placeholder="z. B. 00042" />
            </div>
            <div>
              <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>E-Mail</LabelLine>
              <input className="input mt-2" value={exportCriteria.email} onChange={e => setExportCriteria({ ...exportCriteria, email: e.target.value })} placeholder="mail@combsol.com" />
            </div>
            <div className="flex items-end md:col-span-3">
              <button type="submit" disabled={exportBusy || (!exportCriteria.userId && !exportCriteria.personalnummer && !exportCriteria.email)} className="btn btn-primary">
                {exportBusy ? "Exportiere..." : <><Download size={14} strokeWidth={1.5} /> Als JSON herunterladen</>}
              </button>
            </div>
          </div>
        </form>
      )}

      {showForm && (
        <form onSubmit={handleCreate} className="card p-6 mb-6 animate-in">
          <h3
            className="text-sm font-semibold mb-5"
            style={{ fontFamily: "var(--font-display)", color: "var(--color-foreground)" }}
          >
            Neuen Benutzer anlegen
          </h3>
          {/* Kontotyp-Auswahl */}
          <div className="flex gap-3 mb-5">
            <button
              type="button"
              className={`px-4 py-2 rounded text-xs font-semibold transition-all ${form.authProvider !== "local" ? "ring-2 ring-blue-500" : "opacity-60"}`}
              style={{ background: form.authProvider !== "local" ? "rgba(59,130,246,0.15)" : "var(--color-surface)", color: "var(--color-foreground)", border: "1px solid var(--color-border)" }}
              onClick={() => setForm({ ...form, authProvider: SSO_PROVIDER, password: "" })}
            >
              <span style={{ marginRight: 6 }}>🔑</span> {SSO_LABEL} SSO
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded text-xs font-semibold transition-all ${form.authProvider === "local" ? "ring-2 ring-amber-500" : "opacity-60"}`}
              style={{ background: form.authProvider === "local" ? "rgba(245,158,11,0.15)" : "var(--color-surface)", color: "var(--color-foreground)", border: "1px solid var(--color-border)" }}
              onClick={() => setForm({ ...form, authProvider: "local" })}
            >
              <span style={{ marginRight: 6 }}>⚙️</span> Service Account
            </button>
          </div>
          <p className="text-[11px] mb-4" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
            {form.authProvider !== "local"
              ? `Mitarbeiter meldet sich ueber ${SSO_LABEL === "Microsoft" ? "Microsoft Entra ID" : SSO_LABEL} an. Kein Passwort noetig.`
              : "Technischer Account mit eigenem Passwort (z.B. fuer API-Zugriff, Automatisierung)."}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>Benutzername</LabelLine>
              <input className="input mt-2" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="benutzername" />
            </div>
            {form.authProvider === "local" && (
              <div>
                <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>Passwort</LabelLine>
                <input className="input mt-2" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Min. 10 Zeichen · A–Z · a–z · 0–9" autoComplete="new-password" />
                <p className="text-[11px] mt-1" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
                  mind. 10 Zeichen, Gross- und Kleinbuchstaben, eine Ziffer
                </p>
              </div>
            )}
            <div>
              <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>Name</LabelLine>
              <input className="input mt-2" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Vor- und Nachname" />
            </div>
            <div>
              <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>E-Mail{form.authProvider !== "local" ? " *" : ""}</LabelLine>
              <input className="input mt-2" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder={form.authProvider !== "local" ? (SSO_PROVIDER === "google" ? "name@beispiel.de" : "vorname.nachname@combsol.com") : "optional"} />
              {form.authProvider !== "local" && (
                <p className="text-[11px] mt-1" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
                  {SSO_PROVIDER === "google" ? "Muss mit der Google E-Mail uebereinstimmen" : "Muss mit der Microsoft/Entra-E-Mail uebereinstimmen"}
                </p>
              )}
            </div>
            <div>
              <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>Rolle (Funktion)</LabelLine>
              <select className="input mt-2" value={form.roleId} onChange={e => setForm({ ...form, roleId: Number(e.target.value) })}>
                <option value={0}>Rolle waehlen...</option>
                {roles.map((role) => <option key={role.id} value={role.id}>{role.label ?? role.key}</option>)}
              </select>
              <p className="text-[11px] mt-1" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
                App-Freigaben richten sich nach der gewählten Rolle und werden in „Rollen und App-Zugriff“ gepflegt.
              </p>
            </div>
            <div className="flex items-end">
              <button type="submit" disabled={createMutation.isPending} className="btn btn-success w-full">
                {createMutation.isPending ? "Erstellt..." : "Benutzer erstellen"}
              </button>
            </div>
          </div>

          {formError && (
            <div
              role="alert"
              className="mt-4 p-3 text-xs"
              style={{
                background: "var(--color-danger-bg)",
                color: "var(--color-danger)",
                border: "1px solid var(--color-danger)",
                borderRadius: "var(--radius-sm, 4px)",
                fontFamily: "var(--font-mono)",
              }}
            >
              {formError}
            </div>
          )}
        </form>
      )}

      {pwSuccess && (
        <div
          role="status"
          className="mb-4 p-3 text-xs"
          style={{
            background: "var(--color-success-bg)",
            color: "var(--color-success)",
            border: "1px solid var(--color-success)",
            borderRadius: "var(--radius-sm, 4px)",
            fontFamily: "var(--font-mono)",
          }}
        >
          {pwSuccess}
        </div>
      )}

      <div className="card overflow-x-auto" data-testid="admin-users">
        <table className="table">
          <thead>
            <tr>
              <th>Benutzer</th><th>Name</th><th>E-Mail</th>
              <th>Rolle</th><th>MFA</th><th>Status</th><th>Letzter Login</th><th></th>
            </tr>
          </thead>
          <tbody>
            {userRows.map((u) => (
              <tr key={u.id} data-testid={`admin-user-${u.id}`} style={u.isActive === false ? { opacity: 0.55 } : undefined}>
                <td>
                  <div className="font-medium">{u.username}</div>
                  <UserIdCopyField userId={u.id} onCopied={setCopyToast} />
                </td>
                <td>{u.name ?? <span style={{ color: "var(--color-muted)" }}>—</span>}</td>
                <td style={{ color: "var(--color-muted)" }}>{u.email ?? "—"}</td>
                <td>
                  <span className="badge" style={{ background: "rgba(0,163,224,0.1)", color: "var(--color-primary)" }}>
                    <ShieldCheck size={11} strokeWidth={1.5} /> {roleLabel(u.roleId, u.roleName)}
                  </span>
                  {roles.length > 0 && roleAssignments.length >= 0 && (() => {
                    const current = roleAssignments.find((assignment) => assignment.userId === u.id);
                    const currentRoleId = current?.roleId ?? "";
                    return (
                      <select
                        value={currentRoleId}
                        onChange={e => {
                          const roleId = Number(e.target.value);
                          if (!roleId || roleId === currentRoleId) return;
                          if (!confirm(`Rolle aendern? Die aktuelle Session des Users wird sofort beendet.`)) return;
                          setUserRoleMutation.mutate({ userId: u.id, roleId });
                        }}
                        title="Rolle"
                        className="mt-1 text-[11px]"
                        style={{
                          fontFamily: "var(--font-mono)",
                          background: "transparent",
                          color: "var(--color-muted-foreground)",
                          border: "1px dashed var(--color-border)",
                          borderRadius: 4,
                          padding: "1px 4px",
                          display: "block",
                          maxWidth: 100,
                        }}
                      >
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>Rolle: {role.label ?? role.key}</option>
                        ))}
                      </select>
                    );
                  })()}
                </td>
                <td>
                  {u.totpEnabled
                    ? <span className="badge" style={{ background: "var(--color-success-bg)", color: "var(--color-success)" }}>Aktiv</span>
                    : <span className="text-xs" style={{ color: "var(--color-muted)" }}>—</span>
                  }
                </td>
                <td>
                  {u.isActive === false
                    ? <span className="badge" style={{ background: "var(--color-danger-bg)", color: "var(--color-danger)" }}>Deaktiviert</span>
                    : <span className="badge" style={{ background: "var(--color-success-bg)", color: "var(--color-success)" }}>Aktiv</span>
                  }
                </td>
                <td className="text-xs" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
                  {u.lastSignedIn ? new Date(u.lastSignedIn).toLocaleDateString("de") : "—"}
                </td>
                <td>
                  <div className="flex gap-1">
                    {u.isActive !== false && (
                      <button
                        onClick={() => { setPwResetTarget({ id: u.id, username: u.username }); setPwError(null); setPwNew(""); setPwConfirm(""); }}
                        title="Passwort zuruecksetzen"
                        className="btn btn-secondary py-1 px-2 text-xs"
                      >
                        <Key size={12} strokeWidth={1.5} />
                      </button>
                    )}
                    {u.isActive === false ? (
                      <button
                        onClick={() => reactivateMutation.mutate({ id: u.id })}
                        title="Reaktivieren"
                        className="btn btn-secondary py-1 px-2 text-xs"
                      >
                        <RotateCcw size={12} strokeWidth={1.5} />
                      </button>
                    ) : (
                      <button
                        onClick={() => { if (confirm("Benutzer deaktivieren? (Soft-Delete – kann reaktiviert werden)")) deleteMutation.mutate({ id: u.id }); }}
                        title="Deaktivieren"
                        className="btn btn-danger py-1 px-2 text-xs"
                      >
                        <Trash2 size={12} strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {copyToast && <CopyToast message={copyToast} onDismiss={dismissCopyToast} />}

      {pwResetTarget && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closePwModal}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 60,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}
        >
          <form
            onSubmit={handlePwSubmit}
            onClick={e => e.stopPropagation()}
            className="card animate-in"
            style={{ width: "100%", maxWidth: 480, padding: 24 }}
          >
            <div className="flex items-center justify-between mb-1">
              <h3
                className="text-sm font-semibold"
                style={{ fontFamily: "var(--font-display)", color: "var(--color-foreground)" }}
              >
                Passwort zuruecksetzen
              </h3>
              <button type="button" onClick={closePwModal} className="p-1 cursor-pointer" style={{ color: "var(--color-muted)" }}>
                <X size={14} strokeWidth={1.5} />
              </button>
            </div>
            <p className="text-xs mb-5" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-body)" }}>
              Fuer Benutzer <strong style={{ color: "var(--color-foreground)" }}>{pwResetTarget.username}</strong>.
              Nach dem Speichern wird dessen aktuelle Session beendet (Force-Logout) und der Vorgang im Audit-Log
              als <code style={{ fontFamily: "var(--font-mono)" }}>admin.user.password_reset</code> gefuehrt.
            </p>
            <div className="flex flex-col gap-4">
              <div>
                <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>Neues Passwort</LabelLine>
                <input
                  type="password"
                  className="input mt-2"
                  value={pwNew}
                  onChange={e => setPwNew(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Min. 12 Zeichen · A–Z · a–z · 0–9"
                  autoFocus
                />
              </div>
              <div>
                <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>Wiederholung</LabelLine>
                <input
                  type="password"
                  className="input mt-2"
                  value={pwConfirm}
                  onChange={e => setPwConfirm(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Passwort erneut eingeben"
                />
              </div>
              <p className="text-[11px]" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
                mindestens 12 Zeichen, Gross- und Kleinbuchstaben, mindestens eine Ziffer
              </p>
            </div>

            {pwError && (
              <div
                role="alert"
                className="mt-4 p-3 text-xs"
                style={{
                  background: "var(--color-danger-bg)",
                  color: "var(--color-danger)",
                  border: "1px solid var(--color-danger)",
                  borderRadius: "var(--radius-sm, 4px)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {pwError}
              </div>
            )}

            <div className="flex gap-2 justify-end mt-5">
              <button type="button" className="btn btn-secondary" onClick={closePwModal}>Abbrechen</button>
              <button type="submit" className="btn btn-primary" disabled={resetPasswordMutation.isPending}>
                {resetPasswordMutation.isPending ? "Setze zurueck..." : "Passwort setzen & ausloggen"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
